import React, { createContext, useState, useContext, useEffect } from 'react'
import { createClient } from '@supabase/supabase-js'
import { toast } from 'sonner'
import config from './config'
// Initialize Supabase client with environment variables
const supabaseUrl = config.supabaseUrl
const supabaseAnonKey = config.supabaseAnonKey

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Missing Supabase URL or Anonymous Key. Please check your environment variables.'
  )
}

const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
})

const SupabaseContext = createContext(null)

export const useSupabase = () => {
  const context = useContext(SupabaseContext)
  if (!context) {
    throw new Error('useSupabase must be used within a SupabaseProvider')
  }
  return context
}

export const SupabaseProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)

  useEffect(() => {
    // Get initial session
    const initSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession()

        if (!error) {
          setSession(data.session)
          setUser(data.session?.user ?? null)
        } else {
          console.error('Session initialization error:', error)
        }

        setLoading(false)
      } catch (err) {
        console.error('Unexpected error during session init:', err)
        setLoading(false)
      }
    }

    initSession()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // console.log('Auth state changed:', event, session?.user?.email)

      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)

      // Handle specific auth events
      if (event === 'SIGNED_IN') {
        // console.log('User signed in successfully')
      } else if (event === 'SIGNED_OUT') {
        // console.log('User signed out')
        // Clear any stored form data on sign out
        sessionStorage.removeItem('pendingFormData')
        sessionStorage.removeItem('ipCity')
      } else if (event === 'TOKEN_REFRESHED') {
        // console.log('Token refreshed')
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const handleGoogleSignIn = async (formData) => {
    try {
      setLoading(true)

      // Validate form data before proceeding
      if (formData && (!formData.city?.trim() || !formData.company?.trim())) {
        toast.error('Please fill in all required fields before signing in')
        setLoading(false)
        return
      }

      // Store form data in sessionStorage before redirect
      if (formData) {
        sessionStorage.setItem('pendingFormData', JSON.stringify(formData))
        // console.log('Form data stored for Google sign-in')
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      })

      if (error) {
        throw error
      }

      // Note: The redirect will happen automatically, so code after this won't execute
    } catch (error) {
      console.error('Error with Google sign-in:', error)

      // Handle specific error types
      if (error.message?.includes('popup')) {
        toast.error('Please allow popups for this site and try again')
      } else if (error.message?.includes('network')) {
        toast.error(
          'Network error. Please check your connection and try again.'
        )
      } else {
        toast.error('Failed to sign in with Google. Please try again.')
      }

      setLoading(false)
    }
  }

  const signInWithLinkedIn = async (formData) => {
    try {
      setLoading(true)

      // Validate form data before proceeding
      if (formData && (!formData.city?.trim() || !formData.company?.trim())) {
        toast.error('Please fill in all required fields before signing in')
        setLoading(false)
        return
      }

      // Store form data in sessionStorage before redirect
      if (formData) {
        sessionStorage.setItem('pendingFormData', JSON.stringify(formData))
        // console.log('Form data stored for LinkedIn sign-in')
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            scope: 'openid profile email',
          },
        },
      })

      if (error) {
        throw error
      }

      // Note: The redirect will happen automatically, so code after this won't execute
    } catch (error) {
      console.error('Error with LinkedIn sign-in:', error)

      // Handle specific error types
      if (error.message?.includes('popup')) {
        toast.error('Please allow popups for this site and try again')
      } else if (error.message?.includes('network')) {
        toast.error(
          'Network error. Please check your connection and try again.'
        )
      } else {
        toast.error('Failed to sign in with LinkedIn. Please try again.')
      }

      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      const { error } = await supabase.auth.signOut()

      if (error) {
        console.error('Sign out error:', error)
        toast.error('Error signing out')
      } else {
        toast.success('Signed out successfully')
      }
    } catch (error) {
      console.error('Unexpected sign out error:', error)
      toast.error('Error signing out')
    } finally {
      setLoading(false)
    }
  }

  // Helper function to check if user is authenticated
  const isAuthenticated = () => {
    return !!user && !!session
  }

  // Helper function to get user metadata
  const getUserMetadata = () => {
    return user?.user_metadata || {}
  }

  return (
    <SupabaseContext.Provider
      value={{
        user,
        loading,
        session,
        signInWithLinkedIn,
        handleGoogleSignIn,
        signOut,
        supabase,
        isAuthenticated,
        getUserMetadata,
      }}
    >
      {children}
    </SupabaseContext.Provider>
  )
}
